/* eslint-disable max-lines */
import { useIsFocused } from '@react-navigation/native';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  isAfter,
  isBefore,
  isSameDay,
  startOfMonth,
} from 'date-fns';
import {
  useFocusEffect,
  useNavigationContainerRef,
  usePathname,
  useRouter,
} from 'expo-router';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ScrollView } from 'react-native';
import { useSharedValue } from 'react-native-reanimated';

import { Box, VStack, theme } from '@squire/ui';

import { getProfessionalsQuery } from '@/api/booking/getProfessionalsQuery';
import { getServicesQuery } from '@/api/booking/getServicesQuery';
import type { UpsertCartResponse } from '@/api/booking/upsertCartMutation/types';
import { shopDetailQuery } from '@/api/shop/shopDetailQuery';
import { type BottomSheetRef } from '@/components/_shared/BottomSheet';
import { FullScreenLoading } from '@/components/_shared/FullScreenLoading';
import { useToast } from '@/components/_shared/Toaster';
import type { UserInformationFormValues } from '@/components/_shared/UserInformationForm/types';
import { ViewOrderBar } from '@/components/_shared/ViewOrderBar';
import { BnplBottomSheet } from '@/components/booking/BnplBottomSheet';
import { BookingConsentSheet } from '@/components/booking/BookingConsentSheet';
import { CashAppBottomSheet } from '@/components/booking/CashAppBottomSheet';
import { CheckoutFormBottomSheet } from '@/components/booking/CheckoutFormBottomSheet';
import { ChooseTimeCalendar } from '@/components/booking/ChooseTimeCalendar';
import { ChooseTimeCalendarSkeleton } from '@/components/booking/ChooseTimeCalendarSkeleton/ChooseTimeCalendarSkeleton';
import { ChooseTimeGuestSection } from '@/components/booking/ChooseTimeGuestSection';
import { ChooseTimeLoadingScreen } from '@/components/booking/ChooseTimeLoadingScreen';
import { ConfirmBookingBottomSheet } from '@/components/booking/ConfirmBookingBottomSheet';
import { StandbyWaitlistJoinSheet } from '@/components/booking/StandbyWaitlistJoinSheet';
import { ViewOrderBottomSheet } from '@/components/booking/ViewOrderBottomSheet';
import { routes } from '@/constants/routes';
import { storage, storageKeys } from '@/constants/storage';
import {
  markCheckoutStarted,
  useOptimisticCartUpdater,
} from '@/hooks/booking/useOptimisticCartUpdater';
import { useReservationInterval } from '@/hooks/booking/useReservationInterval';
import { useUpdateCustomerProfileMutation } from '@/hooks/mutations/customer/useUpdateCustomerProfileMutation';
import { useBnplEligibility } from '@/hooks/payments/useBnplEligibility';
import type { Customer } from '@/hooks/queries/customer';
import { useCustomerQuery } from '@/hooks/queries/customer/useCustomerQuery';
import { useDebounce } from '@/hooks/shared/useDebounce';
import { useOnMount } from '@/hooks/shared/useOnMount';
import { useUnfocusCallback } from '@/hooks/shared/useUnfocusCallback';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import type { CartItem } from '@/types/booking';
import {
  dayHasAfterHours,
  getBarberOffDates,
  isAnyProfessionalWorkingOnDate,
} from '@/utils/barberScheduleUtils';
import type { BnplMethodType } from '@/utils/bnpl';
import { canTipCartItem } from '@/utils/bookingCartUtils';
import { getCheckoutErrorContent } from '@/utils/checkoutErrors';
import {
  DateFormat,
  formatDate,
  formatDateWithTimezone,
  getToday,
  parseDate,
} from '@/utils/dateUtils';
import { isWeb } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';
import { hrefToString, resetToViewReservation } from '@/utils/navigationUtils';
import type { PaymentMethod } from '@/utils/paymentMethods';
import { findNextAvailableDate } from '@/utils/scheduleRangeUtils';
import { Tracker } from '@/utils/tracking';
import {
  type WaitlistTimePreference,
  getOfferedTimePreferences,
  normalizeTimePreferences,
} from '@/utils/waitlistTimePreferences';

import { styles } from './styles';
import type {
  ChooseTimeScreenBookData,
  ChooseTimeScreenProps,
  WaitlistSelection,
} from './types';
import { useBnplAbandonedRecovery } from './useBnplAbandonedRecovery';
import { useBookingPayment } from './useBookingPayment';
import { useBookingScheduleQueries } from './useBookingScheduleQueries';
import { useCheckoutBooking } from './useCheckoutBooking';
import { useCheckoutFlowHandlers } from './useCheckoutFlowHandlers';
import { useStandbyWaitlistConsent } from './useStandbyWaitlistConsent';
import { useStandbyWaitlistJoin } from './useStandbyWaitlistJoin';
import { useStandbyWaitlistMode } from './useStandbyWaitlistMode';

// authReturnPath is stored as `${pathname}?${query}`. Consume it only on the
// screen that set it, so a stale key from an abandoned/other booking doesn't
// spuriously reopen this screen's sheets (SQR-12952).
const storedPathMatches = (stored: string, current: string): boolean =>
  stored.split('?')[0] === current;

function hasCustomerProfileChanged(
  formData: UserInformationFormValues,
  customer: Customer,
): boolean {
  return (
    formData.phone !== customer.phone ||
    formData.firstName !== customer.firstName ||
    formData.lastName !== customer.lastName ||
    formData.email !== customer.email
  );
}

export function ChooseTimeScreen({
  promoId,
  shopId: shopRoute,
  cartId,
  barberId,
  anyTime,
  preselectedTime,
  testID,
}: ChooseTimeScreenProps) {
  const { t } = useScreenTranslations('ChooseTimeScreen');
  const { toast } = useToast();

  const router = useRouter();
  const navigationRef = useNavigationContainerRef();
  const pathname = usePathname();

  // Shop and professionals (barber days off, waitlist gating) use the same
  // 60s window as availability instead of refetching on every visit.
  const { data: shop } = useSuspenseQuery({
    ...shopDetailQuery(shopRoute),
    staleTime: 60 * 1000,
  });
  const { data: servicesData } = useSuspenseQuery(getServicesQuery(shop.id));
  // Fetch professionals to filter barbers that don't allow multiple services in "any barber" bookings.
  const { data: professionals, isFetching: isProfessionalsFetching } = useQuery(
    {
      ...getProfessionalsQuery(shop.id),
      staleTime: 60 * 1000,
    },
  );
  const isStandbyWaitlistMode = useStandbyWaitlistMode(shop.id);

  const confirmBookingBottomSheetRef = useRef<BottomSheetRef>(null);
  const checkoutFormBottomSheetRef = useRef<BottomSheetRef>(null);
  const cashAppBottomSheetRef = useRef<BottomSheetRef>(null);
  const consentBottomSheetRef = useRef<BottomSheetRef>(null);
  const adultConsentBottomSheetRef = useRef<BottomSheetRef>(null);
  const viewOrderBottomSheetRef = useRef<BottomSheetRef>(null);
  const orderSheetIndex = useSharedValue(-1);
  const bnplBottomSheetRef = useRef<BottomSheetRef | null>(null);
  const standbyWaitlistJoinSheetRef = useRef<BottomSheetRef>(null);

  // Track when checkout has started to prevent cart mutations after the cart is consumed
  const hasCheckoutStartedRef = useRef(false);
  // The consent sheet fires onDismiss on both Continue and abort; this tells them apart
  const consentContinuedRef = useRef(false);

  // Anchor "today" to the shop's timezone so the default day, current month and
  // the calendar's earliest selectable day all match the shop's calendar day.
  // Memoized so it's a stable Date across renders (not a fresh one each render).
  const shopToday = useMemo(() => getToday(shop?.timezone), [shop?.timezone]);
  // The shop-local calendar day of a deep-link preselected slot (e.g. Google AI
  // Mode), derived from its UTC instant — matching by this (rather than the
  // device's local day) keeps the day scoping correct across shop/device
  // timezone mismatches. Computed up front (not just below, near its other
  // uses) so the initial currentMonth/selectedDate state can start there
  // directly instead of on today's month and jumping afterward (SQR-16392).
  const preselectedDayKey = preselectedTime
    ? formatDateWithTimezone(
        preselectedTime,
        DateFormat.API_DATE,
        shop.timezone,
      )
    : undefined;
  const [currentMonth, setCurrentMonth] = useState(() =>
    preselectedDayKey
      ? startOfMonth(parseDate(preselectedDayKey))
      : startOfMonth(shopToday),
  );
  const [selectedDate, setSelectedDate] = useState(() =>
    preselectedDayKey ? parseDate(preselectedDayKey) : shopToday,
  );
  // Track expanded state by index (not ID) because cart item IDs change after server responses
  const [expandedGuestIndices, setExpandedGuestIndices] = useState<Set<number>>(
    new Set(),
  );
  const anyTimeSelected = useRef(!anyTime);
  const [hasTipBeenUserSelected, setHasTipBeenUserSelected] = useState(false);

  const {
    isMutatingCart,
    cart,
    isCartInvalid,
    customer,
    setAppointmentTime,
    clearAllAppointmentTimes,
    removeServiceLocal,
    commitCart,
    setTip,
    isLoadingCart,
    setPromoCode,
    removeGiftCardCode,
    setCustomer,
    validateGuestMembership,
    setLocalCustomer,
    addPayment,
    clearPayments,
    addPaymentAndCustomer,
    setTimeAndTip,
    setDefaultTip,
  } = useOptimisticCartUpdater(
    shop.id,
    cartId,
    servicesData?.services,
    promoId,
  );

  useFocusEffect(
    useCallback(() => {
      const authReturnPath = storage.getString(storageKeys.authReturnPath);
      if (authReturnPath && storedPathMatches(authReturnPath, pathname)) {
        storage.remove(storageKeys.authReturnPath);
        // Stagger the reopen so the user sees the sheets appear in sequence
        // (Your Order, then Confirm Booking). Timers cleared on cleanup below.
        const viewOrderTimer = setTimeout(() => {
          viewOrderBottomSheetRef.current?.open();
        }, 500);
        const confirmTimer = setTimeout(() => {
          (isStandbyWaitlistJoinRef.current
            ? standbyWaitlistJoinSheetRef
            : confirmBookingBottomSheetRef
          ).current?.open();
        }, 1000);
        return () => {
          clearTimeout(viewOrderTimer);
          clearTimeout(confirmTimer);
        };
      } else if (storage.getString(storageKeys.bnplReopenConfirm)) {
        storage.remove(storageKeys.bnplReopenConfirm);
        // SQR-12809 workaround: a failed/cancelled BNPL leaves a stale payment on
        // the cart server-side, dropping the outstanding total below the BNPL
        // minimum so the backend reports BNPL ineligible. Re-upsert with empty
        // payments to restore it. Sheet reopening is not blocked on the mutation:
        // if it fails, the user still lands on Confirm Booking (without BNPL),
        // matching prior behaviour. Remove once SQR-12810 fixes the backend.
        clearPayments();
        viewOrderBottomSheetRef.current?.open();
        confirmBookingBottomSheetRef.current?.open();
      } else if (storage.getString(storageKeys.cashAppReopenConfirm)) {
        storage.remove(storageKeys.cashAppReopenConfirm);
        // SQR-15545: on native the Cash App sheet stays open behind the return
        // screen. Close it so the QR unmounts and drops its used intent.
        cashAppBottomSheetRef.current?.close();
        viewOrderBottomSheetRef.current?.open();
        confirmBookingBottomSheetRef.current?.open();
      }
    }, [clearPayments, pathname]),
  );

  const { eligibleMethods } = useBnplEligibility(cart, shop);

  // Providers of the BNPL row selected on the last Book press. Null until a
  // row has been picked, in which case the sheet falls back to everything the
  // session reports (SQR-15886).
  const [bnplProviders, setBnplProviders] = useState<BnplMethodType[] | null>(
    null,
  );

  // Local state for waitlist selections (not persisted to server).
  // Keyed by appointment INDEX (not ID) because the server returns new IDs
  // on every cart upsert. Index-based tracking is stable across mutations.
  const [waitlistSelections, setWaitlistSelections] = useState<
    Map<number, WaitlistSelection>
  >(new Map());

  // Compute a Map of current cart item ID → waitlist day for items that have
  // waitlist selected. Derived from index-based selections + current cart items
  // so it stays correct even as the server returns new item IDs on every upsert.
  // Using Map (instead of Set) lets consumers get the waitlist date via .get(id)
  // while all existing .has(id) checks continue to work unchanged.
  const waitlistSelectedIds = useMemo(() => {
    const ids = new Map<string, string>();
    (cart?.items || []).forEach((item, index) => {
      const selection = waitlistSelections.get(index);
      if (selection) ids.set(item.id, selection.day);
    });
    return ids;
  }, [cart?.items, waitlistSelections]);

  const isStandbyWaitlistJoin =
    isStandbyWaitlistMode && waitlistSelections.size > 0;
  // Read by the focus effect's auth-return timer without adding it to the
  // effect's deps, which would re-run the effect and clear that timer.
  const isStandbyWaitlistJoinRef = useRef(isStandbyWaitlistJoin);
  useEffect(() => {
    isStandbyWaitlistJoinRef.current = isStandbyWaitlistJoin;
  }, [isStandbyWaitlistJoin]);

  const waitlistTimePreferencesById = useMemo(() => {
    const preferences = new Map<string, WaitlistTimePreference[]>();
    (cart?.items || []).forEach((item, index) => {
      const selection = waitlistSelections.get(index);
      if (selection) preferences.set(item.id, selection.timePreferences);
    });
    return preferences;
  }, [cart?.items, waitlistSelections]);

  // Waitlist is available only when the shop has an active waiting list
  // AND no appointment is booked with "Any Barber" (matching WOW behavior).
  const hasWaitlistEnabled =
    !!shop?.waitingListEnabled &&
    (shop?.waiting_lists?.some((waitlist) => waitlist.enabled) ?? false);

  const hasAnyBarberBooking =
    cart?.items?.some((item) => item.bookedWithAnyBarber || !item.barber) ??
    false;

  const waitlistRequiresPayment =
    shop?.waiting_lists
      ?.filter((wl) => wl.enabled)
      .some((wl) => wl.bookAndPayOnly) ?? false;

  // Advance-booking window, driven by the selected barber(s). Also yields the
  // month-aligned fetch/display range so the schedule queries and the calendar
  // extend to the barber's window (e.g. 120 days) instead of a fixed 3 months.
  const {
    end: bookingWindowEnd,
    rangeStart: scheduleRangeStart,
    rangeEnd: scheduleRangeEnd,
    monthsAhead: bookingMonthsAhead,
  } = useReservationInterval(shop, cart);

  const {
    scheduleQueries,
    availabilityMap,
    appointments,
    isRefetchingSchedules,
    refetchSchedules,
    isGroupAppointment,
  } = useBookingScheduleQueries({
    shopId: shop.id,
    cart,
    currentMonth,
    timezone: shop?.timezone,
    rangeStart: scheduleRangeStart,
    rangeEnd: scheduleRangeEnd,
    services: servicesData?.services, // Transform parent IDs to service IDs for correct availability
    professionals, // Filter barbers that don't allow multiple services in "any barber" bookings
    waitlistEnabled:
      isStandbyWaitlistMode || (hasWaitlistEnabled && !hasAnyBarberBooking),
  });

  // Dates (excluding today) where at least one guest's specifically-selected
  // barber is confirmed off — feeds ChooseTimeCalendar's day-disabling/graying.
  const barberOffDates = useMemo(() => {
    if (!professionals) return [];

    const earliestOffDate = addDays(shopToday, 1);
    if (isBefore(bookingWindowEnd, earliestOffDate)) return [];

    const guestBarberScheduleInfos = appointments.map((cartItem) => {
      // "Any barber" guests aren't tied to whichever barber is currently
      // referenced on the cart item — another barber may still serve the
      // booking, so they never contribute a day-off override.
      if (cartItem.bookedWithAnyBarber) return undefined;
      return professionals.find(
        (professional) => professional.barber.id === cartItem.barber?.id,
      )?.barber;
    });

    const dateRange = eachDayOfInterval({
      start: earliestOffDate,
      end: bookingWindowEnd,
    });

    return getBarberOffDates(guestBarberScheduleInfos, dateRange);
  }, [professionals, appointments, shopToday, bookingWindowEnd]);

  const {
    isPayInPerson,
    setIsPayInPerson,
    setPayInPersonToken,
    isCheckingOutCart,
    isCheckoutError,
    isCartExpired,
    checkoutCartError,
    checkoutErrorType,
    handleConsentContinue,
    handleResetCheckout: resetCheckout,
    recordNotificationConsent,
  } = useCheckoutBooking({
    shopId: shop.id,
    shopRoute,
    shopGaMeasurementId: shop.brand?.settings?.gaMeasurementId,
    shopMetaPixelId: shop.brand?.settings?.metaPixelId,
    cartId,
    customer,
    addPayment,
    onCheckoutStart: () => {
      // Set local ref for unfocus callback
      hasCheckoutStartedRef.current = true;
      // Notify the cart updater to suppress error toasts from in-flight mutations
      markCheckoutStarted();
    },
  });

  const {
    handleBook,
    beginWalletPayment,
    abortWalletPayment,
    handleSuccessBookingPayment,
    handlePayInPersonWithCard,
    isJoiningWaitlist,
    handleSuccessWaitlistPayment,
    isProcessingPayment,
    resetProcessingPayment,
    handleCheckoutFormDismiss,
  } = useBookingPayment({
    shop,
    services: servicesData?.services,
    addPayment,
    addPaymentAndCustomer,
    setCustomer,
    setLocalCustomer,
    setIsPayInPerson,
    setPayInPersonToken,
    setBnplProviders,
    requestCardForInPersonPayments:
      shop?.requestCardForInPersonPayments ?? false,
    waitlistRequiresPayment,
    cart: cart ?? null,
    isWaitlistMode: waitlistSelections.size > 0,
    waitlistSelections,
    onWaitlistSuccess: (id) => {
      checkoutFormBottomSheetRef.current?.close();
      router.dismissAll();
      router.push(routes.viewWaitlist({ waitlistCustomerId: id }));
    },
    refs: {
      confirmBookingBottomSheetRef,
      checkoutFormBottomSheetRef,
      cashAppBottomSheetRef,
      consentBottomSheetRef,
      bnplBottomSheetRef,
    },
  });

  // Memoized: the recovery hook derives its automatic web effects from these
  // callbacks, so inline ones would rebuild the trigger on every render and
  // could re-issue a cancel after each failed attempt.
  const handleBnplRecovered = useCallback(async () => {
    // The pending intent was cancelled and the cart is unlocked server-side.
    // Refresh the cart (outstanding total + BNPL eligibility) and put the
    // user back on Confirm Booking, mirroring the bnpl-return cancel path.
    // The refresh is awaited and the Book button released only afterwards,
    // so the button holds one continuous loading state through the whole
    // recovery instead of flickering off and on between its phases —
    // `clearPayments` is fire-and-forget, hence the explicit settle promise.
    bnplBottomSheetRef.current?.close();
    try {
      // Reject on failure so the hook keeps the session record: the intent is
      // gone but the cart still carries the stale payment, so the next trigger
      // must be able to find it and retry the cleanup.
      await new Promise<void>((resolve, reject) => {
        clearPayments({
          onSuccess: () => resolve(),
          onError: (error) => reject(error),
        });
      });
    } finally {
      resetProcessingPayment();
    }
    viewOrderBottomSheetRef.current?.open();
    confirmBookingBottomSheetRef.current?.open();
  }, [clearPayments, resetProcessingPayment]);

  const handleBnplAlreadyPaid = useCallback(
    (saleOrderId: string) => {
      // The customer completed the payment at the provider (e.g. in another
      // tab) — never restart checkout; show them their reservation. Reset
      // rather than replace so the reservation's close button lands on Home
      // (SQR-15312).
      resetToViewReservation(navigationRef, { saleOrderId, bnpl: 'true' });
    },
    [navigationRef],
  );

  // A native retry cancelled its leftover intent, which detached the stale
  // payment server-side. Refresh the cart and hand the sheet the amount now
  // outstanding — its own cart prop is a stale snapshot reading 0.
  //
  // `clearPayments` runs a fire-and-forget mutation (it resolves to nothing),
  // so the refreshed total is read from the upsert's own response.
  const handleBnplIntentCanceled = useCallback(
    () =>
      new Promise<number | false>((resolve) => {
        clearPayments({
          onSuccess: (data) => {
            resolve(data?.breakdown?.amountLeftForPayment ?? 0);
          },
          onError: (error) => {
            Logger.error(
              'Failed to refresh cart after cancelling BNPL intent',
              {
                error,
              },
            );
            resolve(false);
          },
        });
      }),
    [clearPayments],
  );

  const { recoverAbandonedBnpl } = useBnplAbandonedRecovery({
    cartId: cart?.id,
    onRecovered: handleBnplRecovered,
    onPaid: handleBnplAlreadyPaid,
  });

  // Deliberate BNPL sheet dismissal — silent, matching Apple Pay and Cash App
  // cancels (no error toast). When the attempt already created a payment
  // intent (the user backed out of the provider flow after Next), the
  // recovery cancels it so the cart unlocks for a retry (SQR-15310):
  // 'recovered' releases the Book button in onRecovered after the cart
  // refresh, and 'paid' navigates away — anything else means nothing is
  // holding the button, so release it here.
  const handleBnplSheetCancel = async () => {
    const outcome = await recoverAbandonedBnpl();
    if (outcome !== 'recovered' && outcome !== 'paid') {
      resetProcessingPayment();
    }
  };

  const { data: loggedInCustomer } = useCustomerQuery();
  const { mutate: syncCustomerProfile } = useUpdateCustomerProfileMutation();

  const syncProfileIfChanged = (data: UserInformationFormValues) => {
    if (
      loggedInCustomer?.id &&
      hasCustomerProfileChanged(data, loggedInCustomer)
    ) {
      syncCustomerProfile({
        customerId: loggedInCustomer.id,
        phone: data.phone,
        firstName: data.firstName || loggedInCustomer.firstName,
        lastName: data.lastName || loggedInCustomer.lastName,
        email: data.email || loggedInCustomer.email,
        preferredLanguage: loggedInCustomer.preferredLanguage ?? 'en',
      });
    }
  };

  const handleBookWithProfileSync = (data: ChooseTimeScreenBookData) => {
    // New attempt, new consent cycle (a zero-page consent never fires onDismiss)
    consentContinuedRef.current = false;

    syncProfileIfChanged(data);
    return handleBook(data);
  };

  const {
    join: joinStandbyWaitlist,
    isJoining: isJoiningStandbyWaitlist,
    recordConsent: recordStandbyConsent,
    clearConsent: clearStandbyConsent,
  } = useStandbyWaitlistJoin({
    shop,
    cart: cart ?? null,
    services: servicesData?.services,
    waitlistSelections,
    shopRoute,
    barberId,
    cartId,
    closeSheets: () => {
      standbyWaitlistJoinSheetRef.current?.close();
      viewOrderBottomSheetRef.current?.close();
    },
  });

  const {
    consentSheetRef: standbyConsentSheetRef,
    consentCustomer: standbyConsentCustomer,
    isReadingConsent: isReadingStandbyConsent,
    askConsent: askStandbyConsent,
    joinAfterConsent: joinStandbyWaitlistAfterConsent,
  } = useStandbyWaitlistConsent({
    shopId: shop.id,
    join: joinStandbyWaitlist,
    clearConsent: clearStandbyConsent,
  });

  const handleStandbyWaitlistJoin = (data: UserInformationFormValues) => {
    syncProfileIfChanged(data);
    askStandbyConsent(data);
  };

  // Check if we already have schedule data for at least one appointment
  const hasScheduleData = scheduleQueries.some(
    (q) => q.data && q.data.length > 0,
  );

  // Only show loading skeleton on initial load, not on refetch after time selection.
  // Once schedule data exists the page is fully loaded — never show the skeleton again
  // regardless of cart mutation/refetch state (e.g. syncCartForWaitlist).
  //
  // The calendar is gated on this same flag rather than a schedule-only one:
  // `scheduleQueries` is derived from `cart.items`, so it is empty while the cart
  // is still loading, and a schedule-only flag reads as "not loading" there. That
  // rendered the real calendar mid-load and then replaced it with the skeleton
  // once the cart landed — skeleton → calendar → skeleton → calendar (SQR-13708
  // follow-up). One flag keeps the skeleton up for the whole first load.
  const isContentLoading =
    !hasScheduleData &&
    (isLoadingCart ||
      scheduleQueries.some((q) => q.isLoading) ||
      !cart?.items?.length);

  // Keeps the calendar interactive while availability for a newly paged month is
  // in flight: `isDateDisabled` treats an unknown day as available only while
  // loading, so without this, paging forward greys out every day of the new
  // month until its response lands (SQR-13708). `isFetching` (not
  // `isRefetching`) because paging changes the query key.
  const isAvailabilityFetching = scheduleQueries.some((q) => q.isFetching);

  // Distinguishes the two things that refetch availability, which otherwise
  // share the single `isRefetchingSchedules` flag:
  //   - Switching day means the schedule on hand has no entry for the newly
  //     selected day, so there is nothing to show and the skeleton stands in.
  //   - Selecting a timeslot leaves the day alone, so its slots stay on screen
  //     and are merely disabled while the API recalculates (the picked slot
  //     keeps its selected styling — `isSelected` wins over `disabled` in
  //     TimeSlotButton).
  //
  // Derived from the response itself rather than tracked in a ref: the selected
  // day updates a render before the refetch becomes observable, so an effect
  // recording "the day we loaded" would latch the new day while its data was
  // still in flight and suppress the skeleton entirely.
  const selectedDayKey = formatDate(selectedDate, DateFormat.API_DATE);
  const hasSelectedDayData = scheduleQueries.some((q) =>
    q.data?.some((entry) => entry.day === selectedDayKey),
  );
  const isDayChanging = isRefetchingSchedules && !hasSelectedDayData;

  const handleDateSelect = (date: Date) => {
    const isDateChanging = !isSameDay(date, selectedDate);
    const hasAnyTimeSelected = appointments.some(
      (appointment) => appointment.dateTime,
    );

    // Update state first before any async operations
    setSelectedDate(date);
    Tracker.track('date_and_time_selected', { date: date.toISOString() });

    // Update currentMonth if the selected date is in a different month
    // This ensures the schedule queries fetch data for the correct month
    const selectedDateMonth = startOfMonth(date);
    if (selectedDateMonth.getTime() !== currentMonth.getTime()) {
      setCurrentMonth(selectedDateMonth);
    }

    if (isDateChanging) {
      // Clear all selected times when changing the date
      // Time slots are specific to a day, so previous selections are no longer valid
      if (hasAnyTimeSelected) {
        clearAllAppointmentTimes();
      }
      // Clear waitlist selections — they are tied to a specific day
      if (waitlistSelections.size > 0) {
        setWaitlistSelections(new Map());
      }
    }
  };

  const handleMonthChange = (month: Date) => setCurrentMonth(month);

  const handleTimeSelect = useCallback(
    (
      appointmentId: string,
      time: Date,
      slotBarberId?: string,
      onSuccess?: (updatedCart: UpsertCartResponse) => void,
    ) => {
      const appointment = appointments.find((a) => a.id === appointmentId);

      // On error, refetch availability so a slot taken by someone else disappears (rollback handled by the cart hook).
      // No onSuccess refetch: the group schedule query's key is built from
      // every guest's barber/services/timeslot (see getGroupScheduleQuery), so
      // the optimistic cart write this mutation performs — and any
      // server-corrected merge back into the cart afterward — already changes
      // that key and triggers a refetch reactively. An explicit refetch here
      // fired a redundant second fetch back-to-back with that automatic one,
      // visibly re-rendering every guest's availability twice per selection.
      const mutationOptions = {
        onError: () => refetchSchedules(),
        ...(onSuccess ? { onSuccess } : {}),
      };

      // Tipping is gated on this appointment's barber, not the shop. On
      // any-barber bookings the cart item has no barber yet at this point —
      // the slot's barber is the one being assigned, so its setting decides
      // (water-on-web reads the any-barber candidate the same way).
      const slotBarberAllowsTips =
        !!slotBarberId &&
        professionals?.find((p) => p.barber.id === slotBarberId)?.barber
          .earlyTipping === true;
      if (
        appointment &&
        (canTipCartItem(appointment) || slotBarberAllowsTips)
      ) {
        // For "any barber" bookings, use the newly assigned barberId
        // For regular bookings, use the existing barberId
        const tipBarberId = slotBarberId || appointment.barberId;
        if (tipBarberId) {
          setTimeAndTip(
            appointmentId,
            time,
            tipBarberId,
            appointment.defaultTip,
            mutationOptions,
          );
          return;
        }
      }

      setAppointmentTime(appointmentId, time, slotBarberId, mutationOptions);
    },
    [
      setAppointmentTime,
      appointments,
      setTimeAndTip,
      refetchSchedules,
      professionals,
    ],
  );

  // The deep-link preselect flow auto-selects a slot as soon as schedule data
  // loads — before `professionals` has necessarily resolved, since a deep link
  // skips ChooseProfessionalScreen entirely and this screen issues that fetch
  // for the first time. When that race means handleTimeSelect can't confirm
  // early-tipping eligibility up front, this reads the barber the upsert
  // response actually resolved (authoritative, no client cache involved) and
  // applies the default tip after the fact (SQR-16492). Scoped to the
  // preselect flow only — manual/"any time" selection already has
  // `professionals` warm in practice and doesn't need this fallback.
  const handlePreselectTimeSuccess = useCallback(
    (updatedCart: UpsertCartResponse) => {
      if (hasTipBeenUserSelected) return;
      const item = updatedCart.items?.[0];
      if (item?.barber?.earlyTipping !== true || item.tipAmount) return;

      // This fires from inside the time-select mutation's own per-call
      // onSuccess, which react-query invokes synchronously from within that
      // same mutation observer's notify cycle. setDefaultTip's upsert call
      // (which passes no per-call options) reuses that SAME shared
      // upsertCartMutation observer — calling it synchronously here
      // overwrites the observer's in-flight #mutateOptions mid-notify,
      // which crashes the ORIGINAL notify call's own subsequent onSettled
      // lookup ("Cannot read property 'onSettled' of undefined", SQR-16492).
      // Deferring lets the current mutation's notify cycle finish unwinding
      // first, so the follow-up upsert starts clean.
      setTimeout(() => setDefaultTip(item.id), 0);
    },
    [hasTipBeenUserSelected, setDefaultTip],
  );

  const handleToggleExpanded = (guestIndex: number) => {
    setExpandedGuestIndices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(guestIndex)) newSet.delete(guestIndex);
      else newSet.add(guestIndex);
      return newSet;
    });
  };

  const handleSelectTip = useCallback(
    (...args: Parameters<typeof setTip>) => {
      setHasTipBeenUserSelected(true);
      setTip(...args);
    },
    [setHasTipBeenUserSelected, setTip],
  );

  // Are there months past the one being viewed still within the BOOKABLE window?
  // Bounded by bookingWindowEnd (the last bookable day), not rangeEnd — rangeEnd
  // is the wider display range, so paging must stop at the real window end.
  const hasUnloadedMonthsAhead = useMemo(
    () => isBefore(startOfMonth(currentMonth), startOfMonth(bookingWindowEnd)),
    [currentMonth, bookingWindowEnd],
  );

  // The earliest loaded date that is after the selected date AND within the
  // booking window (availability is fetched to rangeEnd, which can extend past
  // bookingWindowEnd for short windows). Computed once and reused below.
  const nextAvailableInWindow = useMemo(
    () =>
      findNextAvailableDate(availabilityMap, {
        after: selectedDate,
        maxDate: bookingWindowEnd,
      }),
    [availabilityMap, selectedDate, bookingWindowEnd],
  );

  // Offer "next available" when there's a loaded in-window date OR there are
  // still months ahead left to fetch (lazy loading).
  const hasNextAvailable = !!nextAvailableInWindow || hasUnloadedMonthsAhead;

  const goToNextAvailable = () => {
    if (nextAvailableInWindow) {
      if (nextAvailableInWindow.getMonth() !== selectedDate.getMonth()) {
        handleMonthChange(startOfMonth(nextAvailableInWindow));
      }
      handleDateSelect(nextAvailableInWindow);
      return;
    }

    // Nothing available in the loaded months — fetch-then-jump: advance to the
    // next month (within the bookable window) so its availability loads.
    if (hasUnloadedMonthsAhead) {
      handleMonthChange(startOfMonth(addMonths(currentMonth, 1)));
    }
  };

  const {
    handleContinueToPayment,
    handleAdultConsentComplete,
    handleResetCheckout,
    handleValidatedConsentContinue,
  } = useCheckoutFlowHandlers({
    cart,
    isAdultsOnly: shop?.adultsOnly ?? false,
    shopRoute,
    barberId,
    promoId,
    isCartExpired,
    resetCheckout,
    handleConsentContinue,
    waitlistSelectedIds,
    refs: {
      // The standby sheet takes Confirm Booking's place in the flow, so the
      // adult-consent step and the missing-time guard still run before it.
      confirmBookingBottomSheetRef: isStandbyWaitlistJoin
        ? standbyWaitlistJoinSheetRef
        : confirmBookingBottomSheetRef,
      checkoutFormBottomSheetRef,
      cashAppBottomSheetRef,
      consentBottomSheetRef,
      adultConsentBottomSheetRef,
      viewOrderBottomSheetRef,
    },
  });

  const handleCheckoutFormSubmit = (paymentMethod: PaymentMethod) => {
    if (waitlistSelections.size > 0) {
      handleSuccessWaitlistPayment(paymentMethod);
    } else if (isPayInPerson) {
      handlePayInPersonWithCard(paymentMethod, customer);
    } else {
      handleSuccessBookingPayment(paymentMethod);
    }
  };

  const handleNotificationConsentContinue = () => {
    const result = handleValidatedConsentContinue();
    // undefined = the missing-time guard bailed and checkout never started
    consentContinuedRef.current = result !== undefined;
    return result;
  };

  const handleNotificationConsentDismiss = () => {
    // Closing this marketing-consent prompt means "no thanks", not "cancel":
    // consent is not a condition of purchase, so checkout goes ahead unanswered.
    const continued =
      consentContinuedRef.current ||
      handleNotificationConsentContinue() !== undefined;
    consentContinuedRef.current = false;

    // SQR-15545: the payment is attached before consent, so if checkout can't
    // start it must leave the cart or the booking can't be retried. Gift cards stay.
    const hasCardPayment =
      cart?.payments?.some((payment) => payment.type !== 'gift_card') ?? false;
    if (!continued && hasCardPayment) {
      addPayment(null, {
        onError: () =>
          toast({ variant: 'error', message: t('error.paymentError') }),
      });
    }

    resetProcessingPayment();
  };

  const handleWaitlistPress = async (
    appointmentId: string,
    appointmentBarberId: string,
  ) => {
    const appointmentIndex = (cart?.items || []).findIndex(
      (a) => a.id === appointmentId,
    );
    const appointment = cart?.items[appointmentIndex];

    if (!appointment) return;

    const isCurrentlySelected = waitlistSelections.has(appointmentIndex);

    setWaitlistSelections((prev) => {
      if (prev.has(appointmentIndex)) {
        // Deselect: remove the existing selection
        const newSelections = new Map(prev);
        newSelections.delete(appointmentIndex);
        return newSelections;
      }
      // Only one waitlist entry is allowed per checkout.
      // Replace any existing selection with the new one.
      const newSelections = new Map<number, WaitlistSelection>();
      // Use the local calendar day, not toISOString(): that converts to UTC
      // first, so east of UTC local midnight lands on the previous day.
      newSelections.set(appointmentIndex, {
        barberId: appointmentBarberId,
        day: selectedDayKey,
        timePreferences: [],
      });
      return newSelections;
    });

    // The standby waitlist has no tip, so it leaves the cart untouched.
    if (
      !isCurrentlySelected &&
      !isStandbyWaitlistMode &&
      canTipCartItem(appointment)
    ) {
      setDefaultTip(appointmentId);
    }
  };

  const isWaitlistSelected = (appointmentId: string) => {
    const index = (cart?.items || []).findIndex((a) => a.id === appointmentId);
    return index !== -1 && waitlistSelections.has(index);
  };

  const handleWaitlistTimePreferencesChange = (
    appointmentId: string,
    timePreferences: WaitlistTimePreference[],
  ) => {
    const index = (cart?.items || []).findIndex((a) => a.id === appointmentId);
    setWaitlistSelections((prev) => {
      const selection = prev.get(index);
      if (!selection) return prev;
      return new Map(prev).set(index, { ...selection, timePreferences });
    });
  };

  const allProfessionalBarbers = useMemo(
    () => professionals?.map((professional) => professional.barber) ?? [],
    [professionals],
  );

  // Matches the schedule's "any barber" filter: with more than one service,
  // professionals who take a single service are not offered.
  const getWaitlistBarbers = useCallback(
    (item: CartItem) => {
      if (item.barber && !item.bookedWithAnyBarber) {
        return [
          professionals?.find(
            (professional) => professional.barber.id === item.barber?.id,
          )?.barber,
        ];
      }
      if (item.services.length <= 1) return allProfessionalBarbers;
      return allProfessionalBarbers.filter(
        (barber) => barber.allowMultipleServices !== false,
      );
    },
    [allProfessionalBarbers, professionals],
  );

  // A legacy selection made before the standby settings resolved belongs to
  // the other flow.
  useEffect(() => {
    if (!isStandbyWaitlistMode) return;
    setWaitlistSelections((prev) => (prev.size > 0 ? new Map() : prev));
  }, [isStandbyWaitlistMode]);

  useEffect(() => {
    if (!isStandbyWaitlistMode) return;
    setWaitlistSelections((prev) => {
      let changed = false;
      const next = new Map(prev);
      prev.forEach((selection, index) => {
        const item = cart?.items?.[index];
        const offered = getOfferedTimePreferences(
          !!item && dayHasAfterHours(getWaitlistBarbers(item), selectedDate),
        );
        const timePreferences = normalizeTimePreferences(
          selection.timePreferences,
          offered,
        );
        if (timePreferences.length !== selection.timePreferences.length) {
          changed = true;
          next.set(index, { ...selection, timePreferences });
        }
      });
      return changed ? next : prev;
    });
  }, [isStandbyWaitlistMode, cart?.items, getWaitlistBarbers, selectedDate]);

  // Clear appointment times when navigating away (skip if checkout started)
  useUnfocusCallback(() => {
    if (!hasCheckoutStartedRef.current) {
      clearAllAppointmentTimes();
    }
  });

  useOnMount(() => {
    if (!cartId && !isLoadingCart) {
      // Use replace, not dismissTo: on a non-modal Stack, dismissTo to a route
      // not in the navigation history dismisses out of the app on Android.
      router.replace(
        routes.chooseService({
          shopRoute,
          cart: cartId,
          barberId,
          promoId,
        }),
      );
    }
  });

  // useOnMount fires before schedule queries resolve, so auto-selection must be
  // reactive. anyTimeSelected.current guards against re-running after first select.
  useEffect(() => {
    if (!anyTime || anyTimeSelected.current) return;
    if (scheduleQueries.some((q) => q.isFetching)) return;

    const nextAvailableSuggestedTime = scheduleQueries
      .at(0)
      ?.data?.flatMap((data) => data.suggestedTimes)
      ?.find((data) => data.available);

    if (nextAvailableSuggestedTime?.available) {
      const nextAvailableDate = parseDate(nextAvailableSuggestedTime.time);

      // Only auto-select when the earliest available time is today — jumping
      // ahead to a future day without the user's input skips days they may
      // have wanted to consider.
      if (!isSameDay(nextAvailableDate, shopToday)) return;

      anyTimeSelected.current = true;

      // Update UI state without triggering a mutation
      setSelectedDate(nextAvailableDate);
      const selectedDateMonth = startOfMonth(nextAvailableDate);
      if (selectedDateMonth.getTime() !== currentMonth.getTime()) {
        setCurrentMonth(selectedDateMonth);
      }

      // Set time directly without going through handleDateSelect to avoid race conditions
      // handleDateSelect may call clearAllAppointmentTimes(), which would race with handleTimeSelect
      handleTimeSelect(
        scheduleQueries.at(0)?.guestId || '',
        nextAvailableDate,
        nextAvailableSuggestedTime.barberIds?.[0],
      );
    }
  }, [anyTime, scheduleQueries, currentMonth, handleTimeSelect, shopToday]);

  // Deep-link slot preselection (e.g. Google AI Mode): auto-select the exact
  // time Google showed the customer. If that slot is no longer available by
  // the time this screen loads, fall back to the earliest available slot on
  // that SAME day instead — the deep link is scoped to the day the slot was
  // shown on (SQR-15167), so a fallback must never jump to a different day's
  // availability. Google refreshes availability roughly every 30 min, so a
  // stale slot is expected, and the fallback avoids surfacing an error for it.
  // preselectedTimeHandledRef starts "done" when there's nothing to preselect,
  // and reactive (not useOnMount) for the same reason as the anyTime effect
  // above: schedule data isn't available yet on first render.
  const preselectedTimeHandledRef = useRef(!preselectedTime);
  // SQR-16392: a preselect date past bookingWindowEnd (the barber/shop's real
  // advance-booking limit) will never have a dayEntry — no amount of paging
  // forward loads it, since it's genuinely outside what can be booked. Check
  // this up front so that case doesn't fall through to the "not loaded yet"
  // branch below and wait forever. Compared as shop-calendar days (not the
  // instants) — bookingWindowEnd is itself a device-local end-of-day for a
  // shop calendar day, so comparing raw instants misclassifies a valid last-
  // window-day evening slot whenever the device is ahead of the shop.
  // A date before shopToday is equally unbookable — currentMonth/selectedDate
  // now start on the preselected day directly, so a stale/past link would
  // otherwise land the customer on a bygone month with no data and no path
  // back, instead of today with the same error toast.
  const isPreselectedDateOutsideWindow = !!(
    preselectedDayKey &&
    (isAfter(parseDate(preselectedDayKey), bookingWindowEnd) ||
      isBefore(parseDate(preselectedDayKey), shopToday))
  );
  useEffect(() => {
    if (
      !preselectedTime ||
      !preselectedDayKey ||
      preselectedTimeHandledRef.current
    )
      return;

    // bookingWindowEnd falls back to the shop's (narrower) advanceBookDays
    // until the cart resolves with the specific barber, so acting on it here
    // could wrongly and permanently reject a date that's valid once the real,
    // wider barber window is in.
    if (isLoadingCart) return;

    if (isPreselectedDateOutsideWindow) {
      preselectedTimeHandledRef.current = true;

      // currentMonth/selectedDate start on the preselected day optimistically
      // (before the window is known); now that it's confirmed unbookable,
      // land back on today instead of stranding the customer on a month with
      // nothing they can book.
      const todayMonth = startOfMonth(shopToday);
      if (todayMonth.getTime() !== currentMonth.getTime()) {
        setCurrentMonth(todayMonth);
      }
      setSelectedDate(shopToday);

      // Longer than toastManager's 3s default: this message runs long enough
      // that the default duration cuts off before most people finish reading.
      toast({
        variant: 'error',
        message: t('preselectDateOutsideWindow'),
        duration: 6000,
      });
      return;
    }

    if (scheduleQueries.some((q) => q.isFetching)) return;

    const dayEntry = scheduleQueries
      .at(0)
      ?.data?.find((data) => data.day === preselectedDayKey);
    if (!dayEntry) {
      // The day's month is within the window (checked above) but hasn't been
      // requested — only currentMonth and a one-month prefetch load up front
      // (useBookingScheduleQueries), so a day further out never arrives on
      // its own. Jump there so that month gets fetched; this effect re-runs
      // once it lands.
      const preselectedMonth = startOfMonth(parseDate(preselectedDayKey));
      if (preselectedMonth.getTime() !== currentMonth.getTime()) {
        setCurrentMonth(preselectedMonth);
      }
      return;
    }

    const dayTimes = [
      ...dayEntry.times.morning,
      ...dayEntry.times.afternoon,
      ...dayEntry.times.evening,
    ];
    const exactSlot = dayTimes.find(
      (slot) => slot.time === preselectedTime && slot.available,
    );
    const slotToSelect = exactSlot ?? dayTimes.find((slot) => slot.available);

    preselectedTimeHandledRef.current = true;

    // dayEntry.day is already the shop-local calendar day (matched via
    // preselectedDayKey above) as a plain "yyyy-MM-dd" string with no
    // time-of-day — parseDateWithTimezone would treat it as midnight in the
    // DEVICE's timezone before reapplying shop.timezone, shifting the day
    // back whenever the device is ahead of the shop (SQR-16328). parseDate
    // alone reads its Y/M/D straight into a local Date, matching the
    // convention selectedDate/shopToday use everywhere else in this screen.
    const calendarDay = parseDate(dayEntry.day);
    setSelectedDate(calendarDay);
    const slotMonth = startOfMonth(calendarDay);
    if (slotMonth.getTime() !== currentMonth.getTime()) {
      setCurrentMonth(slotMonth);
    }

    // No available slot that day (day off / fully booked) — still land the
    // client on the linked day so they see it's unavailable (SQR-15167 AC 3),
    // just skip selecting a time.
    if (!slotToSelect) return;

    handleTimeSelect(
      scheduleQueries.at(0)?.guestId || '',
      parseDate(slotToSelect.time),
      slotToSelect.barberIds?.[0],
      handlePreselectTimeSuccess,
    );
  }, [
    preselectedTime,
    preselectedDayKey,
    isLoadingCart,
    isPreselectedDateOutsideWindow,
    handlePreselectTimeSuccess,
    scheduleQueries,
    currentMonth,
    handleTimeSelect,
    shop.timezone,
    shopToday,
    toast,
    t,
  ]);

  // Fire the invalid-cart redirect at most once per mount so a transient null
  // cart after cache restore can't trigger a redirect loop (SQR-12748).
  const hasRedirectedRef = useRef(false);

  // Invalid cart: redirect to services (web uses window.location.replace, native uses router)
  useEffect(() => {
    if (!isCartInvalid || hasRedirectedRef.current) return;

    hasRedirectedRef.current = true;

    if (isWeb()) {
      window.location.replace(
        hrefToString(routes.chooseService({ shopRoute, barberId, promoId })),
      );
    } else {
      router.replace(
        routes.chooseService({
          shopRoute,
          barberId,
          promoId,
        }),
      );
    }
  }, [isCartInvalid, shopRoute, barberId, promoId, router]);

  // If the cart loaded but the current appointment has no services, redirect
  // back to service selection. This happens when the user presses browser back
  // twice (ChooseTime → ChooseService → ChooseProfessional) which clears services,
  // then presses forward again directly to this screen.
  const hasRedirectedForNoServicesRef = useRef(false);
  const isFocused = useIsFocused();

  useEffect(() => {
    if (hasRedirectedForNoServicesRef.current || isLoadingCart) return;
    if (!isFocused) return;
    if (!cart?.items?.length) return;
    const lastItem = cart.items.at(-1);
    if (!lastItem?.services?.length) {
      hasRedirectedForNoServicesRef.current = true;
      router.replace(
        routes.chooseService({
          shopRoute,
          barberId,
          cart: cartId,
          promoId,
        }),
      );
    }
  }, [
    isFocused,
    isLoadingCart,
    cart,
    shopRoute,
    barberId,
    cartId,
    promoId,
    router,
  ]);

  // isCheckingOutCart drops the instant the checkout mutation settles, but the
  // navigation to view-reservation is deferred (rAF, starved ~200ms by
  // post-success work), so the processing overlay would otherwise tear down and
  // flash the underlying screen before the new one mounts (iOS). Debouncing the
  // loading flag holds the overlay across that gap. Gated on !isCheckoutError &&
  // !isCartExpired so the error path is instant — the lagging debounced value
  // can never keep a spinner over the error/reset screen (the overlay still
  // shows for errors via FullScreenLoading's isError, with the error content).
  // Declared before the early returns below to keep hook order stable.
  const debouncedCheckingOut = useDebounce(isCheckingOutCart, 400);
  const isProcessingCheckout =
    (isCheckingOutCart || debouncedCheckingOut) &&
    !isCheckoutError &&
    !isCartExpired;

  const isSyncingAvailability = isMutatingCart || isRefetchingSchedules;
  const debouncedIsSyncingAvailability = useDebounce(
    isSyncingAvailability,
    400,
  );
  const isGuestSectionSyncing =
    isSyncingAvailability || debouncedIsSyncingAvailability;

  if (isCartInvalid) return null;

  const checkoutErrorContent = checkoutErrorType
    ? getCheckoutErrorContent(checkoutErrorType, t)
    : null;

  const [standbyWaitlistEntry] = isStandbyWaitlistJoin
    ? waitlistSelections
    : [];

  return (
    <>
      <ScrollView
        testID={testID}
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <VStack
          space={theme.spacing[8]}
          style={{ paddingBottom: theme.spacing[14] }}
        >
          <Box>
            {isContentLoading ? (
              <ChooseTimeCalendarSkeleton testID="choose-time-calendar-skeleton" />
            ) : (
              <ChooseTimeCalendar
                selectedDate={selectedDate}
                currentMonth={currentMonth}
                availabilityMap={availabilityMap}
                end={bookingWindowEnd}
                rangeEnd={scheduleRangeEnd}
                monthsAhead={bookingMonthsAhead}
                today={shopToday}
                barberOffDates={barberOffDates}
                isGroupAppointment={isGroupAppointment}
                onDateSelect={handleDateSelect}
                onMonthChange={handleMonthChange}
                isLoading={isAvailabilityFetching}
                testID="choose-time-calendar"
              />
            )}
          </Box>

          {/* The skeleton is for loads that replace what's on screen: the first
              load, and switching to a day whose availability isn't in yet.
              Refetching after a timeslot selection keeps the same day's slots
              mounted and disables them via `disabled` below, so the grid
              recalculates in place instead of flashing the skeleton
              (SQR-14091). */}
          {isContentLoading || isDayChanging ? (
            <ChooseTimeLoadingScreen testID="choose-time-loading-screen" />
          ) : (
            <VStack paddingVertical={theme.spacing[2]}>
              {appointments.map((cartItem, index) => {
                const guestSchedule = scheduleQueries.find(
                  (q) => q.guestId === cartItem.id,
                );
                if (!guestSchedule) return null;

                const barberProfessional = professionals?.find(
                  (p) => p.barber.id === cartItem.barber?.id,
                );

                return (
                  <ChooseTimeGuestSection
                    index={index}
                    key={index}
                    cartItem={cartItem}
                    timezone={shop?.timezone}
                    isSingleAppointment={appointments.length === 1}
                    guestSchedule={guestSchedule}
                    selectedDate={selectedDate}
                    isExpanded={expandedGuestIndices.has(index)}
                    onTimeSelect={(time: Date, slotBarberId?: string) =>
                      handleTimeSelect(cartItem.id, time, slotBarberId)
                    }
                    onToggleExpanded={() => handleToggleExpanded(index)}
                    testID={`guest-section-${index}`}
                    onNavigateToNextAvailable={
                      hasNextAvailable ? goToNextAvailable : undefined
                    }
                    disabled={isGuestSectionSyncing}
                    showWaitlist={
                      isStandbyWaitlistMode ||
                      (hasWaitlistEnabled && !cartItem.bookedWithAnyBarber)
                    }
                    barberData={barberProfessional?.barber}
                    onWaitlistPress={handleWaitlistPress}
                    isWaitlistSelected={isWaitlistSelected(cartItem.id)}
                    waitlistDisabled={
                      waitlistSelections.size > 0 &&
                      !isWaitlistSelected(cartItem.id)
                    }
                    barberScheduleInfo={barberProfessional?.barber}
                    isBarberScheduleLoading={isProfessionalsFetching}
                    standbyWaitlist={
                      isStandbyWaitlistMode
                        ? {
                            anyProfessionalWorksOnDay:
                              isAnyProfessionalWorkingOnDate(
                                getWaitlistBarbers(cartItem),
                                selectedDate,
                              ),
                            showAfterHours: dayHasAfterHours(
                              getWaitlistBarbers(cartItem),
                              selectedDate,
                            ),
                            timePreferences:
                              waitlistSelections.get(index)?.timePreferences ??
                              [],
                            onTimePreferencesChange: (next) =>
                              handleWaitlistTimePreferencesChange(
                                cartItem.id,
                                next,
                              ),
                          }
                        : undefined
                    }
                  />
                );
              })}
            </VStack>
          )}
        </VStack>
      </ScrollView>

      <ViewOrderBar
        shop={shop}
        cart={cart || null}
        stage="choose-time"
        testID="choose-time-order-bar"
        isCreatingCart={isMutatingCart}
        waitlistSelectedIds={waitlistSelectedIds}
        hasTipBeenUserSelected={hasTipBeenUserSelected}
        isStandbyWaitlist={isStandbyWaitlistMode && waitlistSelections.size > 0}
        sheetIndex={orderSheetIndex}
        onPress={() => viewOrderBottomSheetRef.current?.open()}
      />
      <ViewOrderBottomSheet
        ref={viewOrderBottomSheetRef}
        stage="choose-time"
        cart={cart || null}
        shop={shop}
        isCreatingCart={isMutatingCart}
        onSelectTip={handleSelectTip}
        onConfirmBooking={handleContinueToPayment}
        waitlistSelectedIds={waitlistSelectedIds}
        onRemoveAddon={removeServiceLocal}
        onCommitEdit={commitCart}
        animatedIndex={orderSheetIndex}
        isStandbyWaitlist={isStandbyWaitlistJoin}
        waitlistTimePreferences={
          isStandbyWaitlistJoin ? waitlistTimePreferencesById : undefined
        }
      />

      <ConfirmBookingBottomSheet
        shop={shop}
        cart={cart ?? null}
        onBook={handleBookWithProfileSync}
        onBookPress={beginWalletPayment}
        onBookSubmitFailed={abortWalletPayment}
        ref={confirmBookingBottomSheetRef}
        onSetPromoCode={setPromoCode}
        removeGiftCardCode={removeGiftCardCode}
        onValidateGuestMembership={validateGuestMembership}
        isLoadingCart={isMutatingCart}
        isCheckingOutCart={isCheckingOutCart || isJoiningWaitlist}
        isBooking={isProcessingPayment}
        isWaitlistMode={waitlistSelections.size > 0}
        waitlistRequiresPayment={waitlistRequiresPayment}
      />

      {isStandbyWaitlistMode && (
        <StandbyWaitlistJoinSheet
          ref={standbyWaitlistJoinSheetRef}
          shop={shop}
          cartItem={
            standbyWaitlistEntry
              ? (cart?.items[standbyWaitlistEntry[0]] ?? null)
              : null
          }
          day={standbyWaitlistEntry?.[1].day ?? selectedDayKey}
          timePreferences={standbyWaitlistEntry?.[1].timePreferences ?? []}
          currency={cart?.currency || shop.brand?.currency || 'USD'}
          isJoining={isJoiningStandbyWaitlist || isReadingStandbyConsent}
          onJoin={handleStandbyWaitlistJoin}
        />
      )}

      {isStandbyWaitlistMode && standbyConsentCustomer && (
        <BookingConsentSheet
          ref={standbyConsentSheetRef}
          testID="standby-waitlist-consent-sheet"
          cart={cart ?? null}
          shop={shop}
          customer={standbyConsentCustomer}
          onContinue={joinStandbyWaitlistAfterConsent}
          alertsToShow={['notification']}
          onNotificationConsentChange={recordStandbyConsent}
        />
      )}

      <CheckoutFormBottomSheet
        customer={customer || undefined}
        shop={shop ?? null}
        cart={cart ?? null}
        ref={checkoutFormBottomSheetRef}
        isSubmitting={isCheckingOutCart || isJoiningWaitlist}
        isBooking={isProcessingPayment}
        onSubmit={handleCheckoutFormSubmit}
        onCancel={() => {
          checkoutFormBottomSheetRef.current?.close();
          // Backed out of card entry — release the Book button loading state.
          resetProcessingPayment();
        }}
        onDismiss={handleCheckoutFormDismiss}
        isWaitlistMode={waitlistSelections.size > 0}
        isPayInPerson={isPayInPerson}
      />

      <CashAppBottomSheet
        ref={cashAppBottomSheetRef}
        onPaymentSuccess={handleSuccessBookingPayment}
        onDismiss={resetProcessingPayment}
        customer={
          customer ?? {
            email: '',
            firstName: '',
            lastName: '',
            phone: '',
            preferredLanguage: '',
          }
        }
        cartParams={{
          cartId: cart?.id ?? '',
          shopId: shop?.id ?? '',
          shopRoute,
          barberId,
          promoId,
        }}
      />

      {cart && shop && (
        <BnplBottomSheet
          ref={bnplBottomSheetRef}
          cart={cart}
          shop={shop}
          addPaymentAndCustomer={addPaymentAndCustomer}
          customer={customer ?? undefined}
          eligibleMethods={bnplProviders ?? eligibleMethods}
          onCancel={handleBnplSheetCancel}
          onAlreadyPaid={handleBnplAlreadyPaid}
          onIntentCanceled={handleBnplIntentCanceled}
          onError={(message) => {
            toast({ variant: 'error', message });
            resetProcessingPayment();
          }}
        />
      )}

      <BookingConsentSheet
        ref={consentBottomSheetRef}
        cart={cart ?? null}
        shop={shop ?? null}
        customer={customer}
        onContinue={handleNotificationConsentContinue}
        onDismiss={handleNotificationConsentDismiss}
        alertsToShow={['notification']}
        onNotificationConsentChange={recordNotificationConsent}
      />

      <BookingConsentSheet
        ref={adultConsentBottomSheetRef}
        cart={cart ?? null}
        shop={shop ?? null}
        customer={customer}
        alertsToShow={['adultOnly']}
        onContinue={handleAdultConsentComplete}
      />

      <FullScreenLoading
        isLoading={isProcessingCheckout || isJoiningWaitlist}
        isError={isCheckoutError || isCartExpired}
        errorTitle={
          isCartExpired
            ? undefined
            : checkoutCartError?.message
              ? t('error.paymentFailedTitle')
              : checkoutErrorContent?.title
        }
        errorMessage={
          isCartExpired
            ? t('error.cartExpired')
            : (checkoutCartError?.message ??
              checkoutErrorContent?.description ??
              t('error.paymentError'))
        }
        onReset={() => {
          // Checkout failed and the user is returning to the flow — release the
          // Book button loading state alongside the checkout reset.
          resetProcessingPayment();
          handleResetCheckout();
        }}
        processingText={t('processing')}
        resetText={isCartExpired ? t('error.startOver') : t('ok')}
      />
    </>
  );
}
